// =============================================================================
// Token Refresh Hook
// =============================================================================
// Proactively refreshes the access token before it expires
// =============================================================================

import { useEffect, useRef } from 'react';
import { useAuthStore } from './useAuth';
import { authService } from '../services/auth.service';

// Access token expires in 15 minutes (900 seconds)
// Refresh 3 minutes before expiry (at 12 minutes = 720 seconds)
const TOKEN_REFRESH_INTERVAL = 12 * 60 * 1000; // 12 minutes in milliseconds

export function useTokenRefresh() {
  const { isAuthenticated, refreshToken, setAccessToken } = useAuthStore();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    const refreshAccessToken = async () => {
      if (isRefreshingRef.current) {
        return;
      }

      isRefreshingRef.current = true;
      console.log('[TokenRefresh] Proactively refreshing token...');

      try {
        // Pass refreshToken from store if available; cookie fallback via withCredentials
        const result = await authService.refreshToken(refreshToken || undefined);
        if (result.accessToken) {
          setAccessToken(result.accessToken);
          console.log('[TokenRefresh] Token refreshed successfully');
        } else {
          // Don't clear auth here - let the API interceptor handle 401s naturally
          console.warn('[TokenRefresh] No access token in response, will retry later');
        }
      } catch (error) {
        console.error('[TokenRefresh] Failed to refresh token:', error);
        // Don't clear auth here - let the API interceptor handle 401s
      } finally {
        isRefreshingRef.current = false;
      }
    };

    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (isAuthenticated) {
      // Start the refresh timer - refresh every 12 minutes (3 minutes before 15-minute expiry)
      // Uses cookie-based refresh as primary, store token as fallback
      timerRef.current = setInterval(refreshAccessToken, TOKEN_REFRESH_INTERVAL);
      console.log('[TokenRefresh] Started refresh timer (every 12 minutes)');

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          console.log('[TokenRefresh] Stopped refresh timer');
        }
      };
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isAuthenticated, refreshToken, setAccessToken]);
}

export default useTokenRefresh;
